export const COMPOSITE_STATUS_MAP = {
  OnHold: [
    'ready-in-under-3m',
    'ready-in-3-5m',
    'ready-in-6-12m',
    'ready-in-12m-plus',
  ],
  LostForever: ['lost-forever'],
}

const ON_HOLD_LEAD_BANNER_FULL_KEY =
  'pages.customers.details.lead-banner.status.inactive'
const LOST_FOREVER_LEAD_BANNER_FULL_KEY =
  'pages.customers.details.lead-banner.status.lost-forever'

/** Status filter keys shown only when "Show deactivated customers" is enabled. */
export const DEACTIVATED_ONLY_STATUS_FILTER_KEYS = ['OnHold', 'LostForever']

const DEACTIVATED_STATUS_FILTER_SLUGS = new Set([
  ...DEACTIVATED_ONLY_STATUS_FILTER_KEYS,
  ...COMPOSITE_STATUS_MAP.OnHold,
  ...COMPOSITE_STATUS_MAP.LostForever,
])

/** Removes On Hold / Lost Forever composite keys and expanded inactive timeframe slugs. */
export function stripDeactivatedStatusFilters(activeStatuses = []) {
  return normalizeActiveStatusesSalesTool(activeStatuses).filter(
    (key) => !DEACTIVATED_STATUS_FILTER_SLUGS.has(key)
  )
}

/**
 * Coerces unknown persisted values to a string array.
 * Handles corrupted `[{}]` from `JSON.stringify([new Set(...)])` and plain `{}` objects.
 */
export function coerceToFilterStringArray(value) {
  if (value == null) {
    return []
  }
  if (value instanceof Set) {
    return [...value].filter((item) => typeof item === 'string' && item.length > 0)
  }
  if (Array.isArray(value)) {
    return value.filter((item) => typeof item === 'string' && item.length > 0)
  }
  return []
}

/**
 * Normalizes activeStatusesSalesTool: keeps valid strings, maps legacy timeframe slugs
 * to OnHold / LostForever, dedupes.
 */
export function normalizeActiveStatusesSalesTool(activeStatuses = []) {
  const strings = coerceToFilterStringArray(activeStatuses)
  const result = []

  strings.forEach((key) => {
    let normalized = key
    if (COMPOSITE_STATUS_MAP.OnHold.includes(key)) {
      normalized = 'OnHold'
    } else if (COMPOSITE_STATUS_MAP.LostForever.includes(key)) {
      normalized = 'LostForever'
    }
    if (!result.includes(normalized)) {
      result.push(normalized)
    }
  })

  return result
}

/** True when value must be rewritten (non-strings, Sets, or legacy slug expansion needed). */
export function activeStatusesFiltersNeedRepair(activeStatuses) {
  if (activeStatuses == null) {
    return false
  }
  if (!Array.isArray(activeStatuses) && !(activeStatuses instanceof Set)) {
    return true
  }
  const hasInvalidEntries = Array.isArray(activeStatuses)
    ? activeStatuses.some((item) => typeof item !== 'string' || item.length === 0)
    : false
  if (hasInvalidEntries) {
    return true
  }
  const cleaned = normalizeActiveStatusesSalesTool(activeStatuses)
  const coerced = coerceToFilterStringArray(activeStatuses)
  return (
    coerced.length !== cleaned.length ||
    coerced.some((value, index) => value !== cleaned[index])
  )
}

/** Keeps only valid dst stage ids (excludes status timeframe slugs leaked via OnHold composite). */
export function sanitizeActiveDstStagesSalesTool(stages, validStageIds = []) {
  const valid = new Set(validStageIds)
  return coerceToFilterStringArray(stages).filter((s) => valid.has(s))
}

/**
 * Magnet inactive lead label (full i18n key). Lost Forever only when timeframe is lost-forever;
 * missing timeframe (legacy users) and on-hold timeframes default to On Hold lead.
 */
export function resolveInactiveMagnetLeadBannerFullKey(inactiveTimeframe) {
  const tf = inactiveTimeframe
  if (tf && COMPOSITE_STATUS_MAP.LostForever.includes(tf)) {
    return LOST_FOREVER_LEAD_BANNER_FULL_KEY
  }
  return ON_HOLD_LEAD_BANNER_FULL_KEY
}

function resolveInactiveLeadBannerStatus(customer, isMagnetBrand) {
  if (!isMagnetBrand) return ON_HOLD_LEAD_BANNER_FULL_KEY
  return resolveInactiveMagnetLeadBannerFullKey(customer.inactiveTimeframe)
}

export function resolveCustomerListingSalesToolStatus(customer, isMagnetBrand = false) {
  if (!customer) return ''
  if (!customer.inactive) return customer.salesToolStatus ?? ''
  return resolveInactiveLeadBannerStatus(customer, isMagnetBrand)
}

export function resolveCustomerListingDstStage(customer) {
  return customer?.dstStage ?? ''
}

export function resolveCustomerListingProjectStatus(customer, isMagnetBrand = false) {
  if (!customer) return ''
  if (!customer.inactive) {
    return isMagnetBrand
      ? (customer.dstStage ?? '')
      : (customer.myProjectStatus ?? '')
  }
  return resolveInactiveLeadBannerStatus(customer, isMagnetBrand)
}

/** Relative keys for `useTranslation('pages.customers.details.lead-banner')` */
export function getLeadBannerStatusTKey(inactive, inactiveTimeframe) {
  if (!inactive) return 'status.active'
  const tf = inactiveTimeframe
  if (tf && COMPOSITE_STATUS_MAP.LostForever.includes(tf)) return 'status.lost-forever'
  return 'status.inactive'
}
